import { useMutation } from "@tanstack/react-query";
import { type Api, assignSetValues } from "api-client";
import { observer } from "mobx-react-lite";
import { type PropsWithChildren, useCallback, useEffect } from "react";
import { useDebouncedCallback } from "use-debounce";

import { useAuthenticatedNavigationContext } from "src/layout/AuthenticatedNavigationContext";
import { useWorkoutEditStore } from "src/workouts/WorkoutEditContext";
import {
  type AssignedValueSnapshot,
  workoutItemSaveContext,
} from "src/workouts/WorkoutItemSetContext.shared";

export const AssignedValuesSaveProvider = observer(
  function AssignedValuesSaveProvider(
    props: PropsWithChildren & { workoutItemId: string },
  ) {
    const workoutEditStore = useWorkoutEditStore();
    const { withSaving } = useAuthenticatedNavigationContext();

    const mutation = useMutation({
      mutationFn: async (payload: Api.AssignSetValues.Input) => {
        return withSaving(
          assignSetValues({
            body: payload,
          }),
        );
      },
    });

    const debouncedMutate = useDebouncedCallback(() => {
      const changedData: AssignedValueSnapshot[] = workoutEditStore
        .getWorkoutItemSets(props.workoutItemId)
        .map((workoutSet) => {
          return {
            workoutSetId: workoutSet.id,
            attributeValues: workoutSet.attributeValues.map(
              (attributeValue) => {
                return {
                  id: attributeValue.id,
                  assignedValue: attributeValue.assignedValue,
                };
              },
            ),
          };
        });

      if (changedData.length === 0) {
        return;
      }

      mutation.mutate({
        workoutSets: changedData,
      });
    }, 300);

    useEffect(() => {
      return () => {
        debouncedMutate.flush();
      };
    }, [debouncedMutate]);

    const requestSave = useCallback(() => {
      debouncedMutate();
    }, [debouncedMutate]);

    return (
      <workoutItemSaveContext.Provider value={requestSave}>
        {props.children}
      </workoutItemSaveContext.Provider>
    );
  },
);
